import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBookingDraft } from '../../contexts/BookingDraftContext';
import {
  getWorkloadLabelAr,
  getDeliveredPhotosLabelAr,
  getShootingMethodLabelAr,
  getProductPhysicalSizeLabelAr,
} from '../../utils/bookingLabels';
import { bookingService } from '../../services/bookingService';
import { pricingService } from '../../services/pricingService';
import { discountCodeService } from '../../services/discountCodeService';
import { supabase } from '../../lib/supabase';
import { sendOTP as sendOTPApi, normalizePhone } from '../../sendOtp';
import { verifyOtpAndSignIn } from '../../services/verifyOtpAndSignIn';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/BackButton';
import Input from '../../components/ui/Input';
import { normalizeOtpValue } from '../../components/OtpInput';
import Image from '../../components/AppImage';
import { formatNumber } from '../../utils/formatNumber';
import Toast from '../../components/ui/Toast';
import {
  validateCheckoutIdentityBeforeOtp,
  isValidCheckoutCustomerEmail,
} from '../../utils/checkoutIdentityValidation';
import { normalizeExperienceLevelToUi, getExperienceLevelLabelAr } from '../../utils/experienceLevel';

// Helper function to format time to Arabic format (ص/م)
const formatTimeToArabic = (time) => {
  if (!time) return 'غير محدد';
  
  // If already formatted, return as is
  if (typeof time === 'string' && (time.includes('ص') || time.includes('م'))) {
    return time;
  }
  
  // Parse time (format: HH:MM or HH:MM:SS)
  const timeStr = String(time);
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours, 10);
  const min = minutes || '00';
  
  if (isNaN(hour)) return time;
  
  if (hour === 0) {
    return `12:${min} ص`;
  } else if (hour < 12) {
    return `${hour}:${min} ص`;
  } else if (hour === 12) {
    return `12:${min} م`;
  } else {
    const displayHour = hour - 12;
    return `${displayHour}:${min} م`;
  }
};

// Helper function to get display labels
const getDisplayLabel = (bookingData, field, value) => {
  // If label exists, use it
  const labelField = `${field}Label`;
  if (bookingData[labelField]) {
    return bookingData[labelField];
  }
  
  // Otherwise, map value to label
  const maps = {
    city: {
      'riyadh': 'الرياض',
      'jeddah': 'جدة',
      'dammam': 'الدمام',
      'mecca': 'مكة المكرمة',
      'medina': 'المدينة المنورة',
      'khobar': 'الخبر',
      'taif': 'الطائف',
      'tabuk': 'تبوك',
      'abha': 'أبها',
      'buraidah': 'بريدة'
    },
    photographyType: {
      'wedding': 'تصوير حفلات الزفاف',
      'portrait': 'التصوير الشخصي',
      'event': 'تصوير المناسبات',
      'commercial': 'التصوير التجاري',
      'real-estate': 'تصوير العقارات',
      'food': 'تصوير الطعام',
      'fashion': 'تصوير الأزياء',
      'sports': 'التصوير الرياضي',
      'product': 'تصوير المنتجات'
    },
    locationType: {
      'indoor': 'داخلي (استوديو أو مكان مغلق)',
      'outdoor': 'خارجي (في الهواء الطلق)',
      'venue': 'قاعة أو مكان فعاليات',
      'client-location': 'موقع العميل'
    },
    duration: {
      '1': 'ساعة واحدة',
      '2': 'ساعتان',
      '3': '3 ساعات',
      '4': '4 ساعات',
      '6': '6 ساعات',
      '8': '8 ساعات',
      '10': '10 ساعات',
      '12': '12 ساعة أو أكثر'
    },
    experienceLevel: {
      'certified': 'معتمد',
      'distinguished': 'مميز',
      'elite': 'نخبة',
      // Legacy
      'beginner': 'معتمد',
      'intermediate': 'مميز',
      'advanced': 'مميز',
      'expert': 'نخبة'
    }
  };
  
  if (maps[field] && maps[field][value]) {
    return maps[field][value];
  }
  
  // Fallback to value if no mapping found
  return value || '';
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const { draft, updateDraft, clearDraft } = useBookingDraft();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [authStep, setAuthStep] = useState('method');
  const [otpSent, setOtpSent] = useState(false);
  const [msegatId, setMsegatId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const verifyOtpInFlightRef = useRef(false);
  const [bookingCreated, setBookingCreated] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState(null);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceLocked, setPriceLocked] = useState(false);
  // SAFE MODE: Track if initial data load from localStorage is complete
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Discount code
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [discountApplying, setDiscountApplying] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null); // { code, percent, amount, subtotal, vat, total }
  const [discountError, setDiscountError] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    fullName: userProfile?.full_name || '',
    phone: userProfile?.phone || '',
    email: userProfile?.email || '',
    invoiceNote: '',
    allowWhatsApp: true,
  });

  // Load booking data from draft or location state
  const bookingData = draft || location.state?.bookingData || {};

  // SAFE MODE: Wait for localStorage to load before checking booking data
  useEffect(() => {
    // Give localStorage time to load (BookingDraftContext loads on mount)
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // SAFE MODE: Don't redirect until initial load is complete
    if (!initialLoadComplete) {
      return;
    }
    
    // If no booking data, redirect to booking wizard
    if (!bookingData || Object.keys(bookingData).length === 0) {
      setToast({
        type: 'error',
        message: 'لا توجد بيانات حجز. يرجى البدء من جديد.',
      });
      setTimeout(() => navigate('/booking-wizard'), 2000);
      return;
    }

    // Save booking data to draft if coming from location state
    if (location.state?.bookingData && !draft) {
      updateDraft(location.state.bookingData);
    }

    // If authenticated, preload user data and skip auth
    if (user && userProfile) {
      setFormData({
        fullName: userProfile.full_name || '',
        phone: userProfile.phone || '',
        email: userProfile.email || '',
        invoiceNote: '',
        allowWhatsApp: true,
      });
      setAuthStep('complete'); // Skip auth steps
    } else {
      setAuthStep('method'); // Show auth if not logged in
    }

    // Calculate and lock price
    calculateAndLockPrice();
  }, [user, userProfile, bookingData, navigate, location.state, draft, updateDraft, initialLoadComplete]);

  // Calculate and lock price
  const calculateAndLockPrice = async () => {
    // If price is already locked within this session, don't recalculate
    if (priceLocked && calculatedPrice) {
      return;
    }

    // Always recalculate from booking parameters — never reuse a cached price from the draft.
    // This ensures going back and changing duration/equipment/city reflects in the final price.
    if (!bookingData.photographyType || !bookingData.city || !bookingData.duration) {
      console.warn('Missing required data for price calculation:', {
        photographyType: bookingData.photographyType,
        city: bookingData.city,
        duration: bookingData.duration
      });
      return;
    }

    try {
      console.log('Calculating price...');
      setPriceLoading(true);

      // Load pricing config (force refresh to get latest multipliers)
      const pricingConfig = await pricingService.getPricingConfig(true);
      
      // Calculate final price using new smart pricing system
      const priceBreakdown = pricingService.calculateFinalPrice(bookingData, pricingConfig);

      console.log('Price breakdown:', priceBreakdown);

      // Lock the price
      const lockedPrice = {
        basePrice: priceBreakdown.baseHourPrice * priceBreakdown.hours,
        vat: priceBreakdown.vatAmount,
        totalPrice: priceBreakdown.total,
        subtotal: priceBreakdown.subtotal,
        breakdown: priceBreakdown.breakdown,
        multipliers: priceBreakdown.multipliers,
        vatPercent: priceBreakdown.vatPercent
      };

      setCalculatedPrice(lockedPrice);
      setPriceLocked(true);
      console.log('Price locked:', lockedPrice);

      // Save locked price to draft with pricing snapshot
      updateDraft({
        ...bookingData,
        price: priceBreakdown.total,
        basePrice: priceBreakdown.baseHourPrice * priceBreakdown.hours,
        vat: priceBreakdown.vatAmount,
        priceLocked: true,
        pricingSnapshot: {
          baseHourPrice: priceBreakdown.baseHourPrice,
          hours: priceBreakdown.hours,
          multipliers: priceBreakdown.multipliers,
          subtotal: priceBreakdown.subtotal,
          vatAmount: priceBreakdown.vatAmount,
          vatPercent: priceBreakdown.vatPercent,
          total: priceBreakdown.total,
          breakdown: priceBreakdown.breakdown,
          workloadBucket: priceBreakdown.workloadBucket || bookingData.workloadBucket || null,
          workloadMultiplier: priceBreakdown.workloadMultiplier || 1.0,
          productPhysicalSizeBucket: priceBreakdown.productPhysicalSizeBucket ?? bookingData.productPhysicalSizeBucket ?? null,
          productPhysicalSizeLabel: bookingData.productPhysicalSizeLabel || null,
        }
      });
    } catch (error) {
      console.error('Failed to calculate price:', error);
      setToast({
        type: 'error',
        message: 'فشل حساب السعر. يرجى المحاولة مرة أخرى.',
      });
      // For testing: set a default price if calculation fails
      const defaultPrice = {
        basePrice: 200,
        vat: 30,
        totalPrice: 230,
        subtotal: 200,
        vatPercent: 15
      };
      setCalculatedPrice(defaultPrice);
      setPriceLocked(true);
    } finally {
      setPriceLoading(false);
    }
  };

  // ── Discount code ─────────────────────────────────────────────────────────
  const handleApplyDiscountCode = async () => {
    const codeTrim = discountCodeInput.trim();
    if (!codeTrim) {
      setDiscountError('يرجى إدخال كود الخصم');
      return;
    }
    if (!calculatedPrice) {
      setDiscountError('يرجى الانتظار حتى يتم حساب السعر');
      return;
    }
    try {
      setDiscountApplying(true);
      setDiscountError('');
      // الخصم يُحسب دائماً على المبلغ الفرعي قبل الضريبة (subtotal)، ثم تُحسب
      // الضريبة على المبلغ بعد الخصم — هذا يعمل بشكل صحيح سواء كانت الضريبة
      // 0% (الوضع الحالي) أو مفعّلة لاحقاً، دون أي تعديل على pricingService.
      const baseSubtotal = calculatedPrice.subtotal ?? calculatedPrice.basePrice ?? 0;
      const vatPercent = calculatedPrice.vatPercent || 0;
      const result = await discountCodeService.validateCode(codeTrim, baseSubtotal);
      const discountedSubtotal = Math.max(0, baseSubtotal - result.discountAmount);
      const discountedVat = Math.round(discountedSubtotal * vatPercent / 100);
      const discountedTotal = discountedSubtotal + discountedVat;
      setDiscountInfo({
        code: result.code,
        percent: result.discountPercent,
        amount: result.discountAmount,
        subtotal: discountedSubtotal,
        vat: discountedVat,
        total: discountedTotal,
      });
      setToast({ type: 'success', message: `تم تطبيق كود الخصم بنجاح (خصم ${result.discountPercent}%)` });
    } catch (error) {
      setDiscountInfo(null);
      setDiscountError(error.message || 'كود الخصم غير صحيح');
    } finally {
      setDiscountApplying(false);
    }
  };

  const handleRemoveDiscountCode = () => {
    setDiscountInfo(null);
    setDiscountCodeInput('');
    setDiscountError('');
  };

  const handleSendOTP = async () => {
    const idCheck = validateCheckoutIdentityBeforeOtp(formData);
    if (!idCheck.ok) {
      setToast({ type: 'error', message: idCheck.message });
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (!normalized) {
      setToast({ type: 'error', message: 'يرجى إدخال رقم الجوال صحيح' });
      return;
    }
    try {
      setLoading(true);
      setToast(null);
      const data = await sendOTPApi(normalized);
      if (!data?.success) {
        throw new Error(data?.error || 'فشل إرسال الرمز');
      }
      setMsegatId(data.id);
      setOtpCode('');
      setOtpSent(true);
      setAuthStep('verify');
      setToast({ type: 'success', message: 'تم إرسال رمز التحقق إلى هاتفك' });
    } catch (error) {
      console.error('OTP send error:', error);
      setToast({
        type: 'error',
        message: error.message || 'فشل إرسال رمز التحقق. يرجى المحاولة مرة أخرى.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = useCallback(async () => {
    if (!otpCode || otpCode.length < 4) {
      setToast({ type: 'error', message: 'يرجى إدخال رمز التحقق الكامل' });
      return;
    }
    const idCheck = validateCheckoutIdentityBeforeOtp(formData);
    if (!idCheck.ok) {
      setToast({ type: 'error', message: idCheck.message });
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (!normalized) {
      setToast({ type: 'error', message: 'رقم الجوال غير صحيح' });
      return;
    }
    try {
      setVerifying(true);
      setToast(null);
      const data = await verifyOtpAndSignIn({
        phone: normalized,
        code: otpCode,
        role: 'customer',
        msegatId,
        userData: {
          full_name: formData.fullName.trim(),
          phone: normalized,
          email: formData.email.trim(),
          allow_whatsapp: formData.allowWhatsApp,
        },
      });
      if (data?.error) throw new Error(data.error);

      // بدلاً من اتباع الـ magic link الذي يُعيد التوجيه للصفحة الرئيسية،
      // نستخرج الـ token ونُصادق مباشرة للبقاء في صفحة الحجز
      if (data?.redirectUrl) {
        const url = new URL(data.redirectUrl);
        const tokenHash = url.searchParams.get('token');
        if (!tokenHash) throw new Error('فشل الحصول على رمز الدخول');
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'email',
        });
        if (verifyError) throw new Error(verifyError.message);
        setAuthStep('complete');
        setToast({ type: 'success', message: 'تم التحقق بنجاح' });
        return;
      }

      if (data?.success) {
        setAuthStep('complete');
        setToast({ type: 'success', message: 'تم التحقق بنجاح' });
        return;
      }
      throw new Error('فشل التحقق');
    } catch (error) {
      console.error('OTP verify error:', error);
      setToast({
        type: 'error',
        message: error.message || 'رمز التحقق غير صحيح. يرجى المحاولة مرة أخرى.',
      });
    } finally {
      setVerifying(false);
    }
  }, [otpCode, formData, msegatId]);

  useEffect(() => {
    if (authStep !== 'verify' || otpCode.length !== 4 || verifying || verifyOtpInFlightRef.current) return;
    verifyOtpInFlightRef.current = true;
    handleVerifyOTP().finally(() => {
      verifyOtpInFlightRef.current = false;
    });
  }, [authStep, otpCode, verifying, handleVerifyOTP]);

  // Confirm booking
  const handleConfirmBooking = async () => {
    console.log('=== 🟢 handleConfirmBooking CALLED ===');
    console.log('📋 Full state check:', {
      user: !!user,
      userId: user?.id,
      authStep,
      fullName: formData.fullName,
      phone: formData.phone,
      priceLocked,
      calculatedPrice: !!calculatedPrice,
      calculatedPriceValue: calculatedPrice?.totalPrice,
      bookingData: {
        shootingPlaceType: bookingData.shootingPlaceType,
        preciseLocation: bookingData.preciseLocation,
        photographyType: bookingData.photographyType,
        city: bookingData.city,
        duration: bookingData.duration,
        date: bookingData.date,
        time: bookingData.time
      }
    });
    
    try {
      setSaving(true);
      setToast(null);

      // Validation
      if (!formData.fullName) {
        console.log('❌ Validation failed: No fullName');
        setToast({ type: 'error', message: 'يرجى إدخال الاسم الأول والأخير' });
        setSaving(false);
        return;
      }

      if (!formData.phone) {
        console.log('❌ Validation failed: No phone');
        setToast({ type: 'error', message: 'يرجى إدخال رقم الهاتف' });
        setSaving(false);
        return;
      }

      if (!isValidCheckoutCustomerEmail(formData.email)) {
        setToast({ type: 'error', message: 'يرجى إدخال بريد إلكتروني صالح' });
        setSaving(false);
        return;
      }

      // Validate location based on shooting place type
      console.log('🔍 Checking location validation:', {
        shootingPlaceType: bookingData.shootingPlaceType,
        preciseLocation: bookingData.preciseLocation,
        lat: bookingData.preciseLocation?.lat,
        lng: bookingData.preciseLocation?.lng,
        latType: typeof bookingData.preciseLocation?.lat,
        lngType: typeof bookingData.preciseLocation?.lng
      });
      
      if (bookingData.shootingPlaceType === 'indoor' || bookingData.shootingPlaceType === 'outdoor') {
        // For indoor/outdoor, precise location is required
        const hasValidLocation = bookingData.preciseLocation && 
                                  bookingData.preciseLocation.lat && 
                                  bookingData.preciseLocation.lng &&
                                  !isNaN(Number(bookingData.preciseLocation.lat)) &&
                                  !isNaN(Number(bookingData.preciseLocation.lng));
        
        if (!hasValidLocation) {
          console.log('❌ Validation failed: Invalid preciseLocation', {
            preciseLocation: bookingData.preciseLocation,
            lat: bookingData.preciseLocation?.lat,
            lng: bookingData.preciseLocation?.lng
          });
          setToast({ 
            type: 'error', 
            message: 'يرجى تحديد الموقع الدقيق في صفحة الحجز قبل التأكيد' 
          });
          setSaving(false);
          return;
        }
        console.log('✅ Location validation passed');
      }
      // For photographer_studio, location will be set from photographer profile

      // Get current user (should be authenticated at this point)
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        setToast({ type: 'error', message: 'يرجى تسجيل الدخول أولاً' });
        setAuthStep('method');
        setSaving(false);
        return;
      }

      // Use locked price (must use calculated price, not from bookingData to prevent tampering)
      // If a discount code was applied, use the discounted total instead.
      let finalPrice = discountInfo?.total ?? (calculatedPrice?.totalPrice || bookingData.price || 0);
      
      console.log('Price check in handleConfirmBooking:', {
        calculatedPriceTotal: calculatedPrice?.totalPrice,
        bookingDataPrice: bookingData.price,
        discountApplied: !!discountInfo,
        finalPrice
      });
      
      // If no price, try to use a default for testing or show error
      if (!finalPrice || finalPrice === 0) {
        console.warn('No price available, using default for testing');
        // For MVP testing: use default price if calculation failed
        finalPrice = 1000; // Default price
        console.log('Using default price:', finalPrice);
      }

      const checkoutExpUi = normalizeExperienceLevelToUi(bookingData.experienceLevel) || bookingData.experienceLevel || 'distinguished';
      const checkoutExpLabel = bookingData.experienceLevelLabel || getExperienceLevelLabelAr(checkoutExpUi);

      // Always build a fresh pricing snapshot from the current calculatedPrice — never reuse cached/old snapshots
      const pricingSnapshot = {
        baseHourPrice: calculatedPrice.basePrice / (calculatedPrice.breakdown?.find(b => b.type === 'hours')?.value || 1),
        hours: calculatedPrice.breakdown?.find(b => b.type === 'hours')?.value || 1,
        multipliers: calculatedPrice.multipliers || {},
        subtotal: discountInfo?.subtotal ?? (calculatedPrice.subtotal || calculatedPrice.basePrice),
        vatAmount: discountInfo?.vat ?? calculatedPrice.vat,
        vatPercent: calculatedPrice.vatPercent || 15,
        total: finalPrice,
        breakdown: calculatedPrice.breakdown || [],
        subtotalBeforeDiscount: discountInfo ? (calculatedPrice.subtotal || calculatedPrice.basePrice) : null,
        discountCode: discountInfo?.code || null,
        discountPercent: discountInfo?.percent || null,
        discountAmount: discountInfo?.amount || null,
        photographyType: bookingData.photographyType,
        city: bookingData.city,
        locationType: bookingData.locationType,
        duration: bookingData.duration,
        experienceLevel: checkoutExpUi,
        experienceLevelLabel: checkoutExpLabel,
        equipment: bookingData.equipment || [],
        shootingMethods: bookingData.shootingMethods || (bookingData.shootingMethod ? [bookingData.shootingMethod] : []),
        shootingMethod: bookingData.shootingMethod || null,
        shootingPlaceType: bookingData.shootingPlaceType || null,
        workloadBucket: bookingData.workloadBucket || null,
        workloadMultiplier: calculatedPrice.multipliers?.workload?.multiplier || 1.0,
        productPhysicalSizeBucket: bookingData.productPhysicalSizeBucket || null,
        productPhysicalSizeLabel: bookingData.productPhysicalSizeLabel || null,
        deliverableMode: bookingData.deliverableMode || 'custom',
        photosCount: bookingData.photosCount ?? 0,
        videosCount: bookingData.videosCount ?? 0,
        shortVideosCount: bookingData.shortVideosCount ?? 0,
        deliverableLabel: bookingData.deliverableLabel || null,
        deliverables: calculatedPrice.deliverables || null,
        calculatedAt: new Date().toISOString(),
        version: '2.1'
      };

      // Create booking with photographer confirmation flow
      // Pass all required data according to PROMPT requirements
      const bookingPayload = {
        customerId: currentUser.id,
        photographyType: bookingData.photographyType || bookingData.serviceType || 'تصوير',
        city: bookingData.city || 'الرياض',
        locationType: bookingData.locationType || 'موقع عام',
        location: bookingData.location || `${bookingData.locationType || 'موقع'}، ${bookingData.city || 'الرياض'}`,
        locationDetails: formData.invoiceNote || null,
        date: bookingData.date || bookingData.bookingDate,
        bookingDate: bookingData.date || bookingData.bookingDate,
        time: bookingData.time || '10:00',
        bookingTime: bookingData.time || '10:00',
        duration: bookingData.duration || '2 ساعة',
        equipment: bookingData.equipment || [],
        experienceLevel: checkoutExpUi,
        finalPrice: finalPrice,
        price: finalPrice,
        basePrice: calculatedPrice?.basePrice || finalPrice * 0.85,
        vat: calculatedPrice?.vat || finalPrice * 0.15,
        pricingSnapshot: pricingSnapshot,
        notes: formData.invoiceNote || null,
        // Add shooting place type and location data
        shootingPlaceType: bookingData.shootingPlaceType,
        preciseLocation: bookingData.preciseLocation,
        photographerStudioLocationSnapshot: bookingData.photographerStudioLocationSnapshot,
        // Add workload data
        workloadBucket: bookingData.workloadBucket || null,
        workloadMultiplier: pricingSnapshot.workloadMultiplier || 1.0,
        productPhysicalSizeBucket: bookingData.productPhysicalSizeBucket || null,
        productPhysicalSizeLabel: bookingData.productPhysicalSizeLabel || null,
        // SAFE MODE: If a photographer was selected from results, use them directly
        // This ensures the selected photographer is assigned, not a random one from RPC
        assignedPhotographerId: bookingData.photographerId || null,
      };
      
      console.log('🔍 Photographer selection:', {
        photographerId: bookingData.photographerId,
        photographerName: bookingData.photographerName,
        hasSelectedPhotographer: !!bookingData.photographerId
      });

      console.log('🚀 Creating booking with payload:', bookingPayload);
      console.log('🚀 About to call bookingService.createBooking...');
      
      let createdBooking;
      try {
        // Add timeout to prevent infinite hang
        const createBookingWithTimeout = Promise.race([
          bookingService.createBooking(bookingPayload),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('انتهت مهلة إنشاء الحجز. يرجى المحاولة مرة أخرى.')), 60000)
          )
        ]);
        
        createdBooking = await createBookingWithTimeout;
        console.log('✅ Booking created successfully:', createdBooking);
      } catch (bookingError) {
        console.error('❌ bookingService.createBooking failed:', bookingError);
        console.error('❌ Error details:', {
          message: bookingError?.message,
          code: bookingError?.code,
          details: bookingError?.details,
          hint: bookingError?.hint,
          stack: bookingError?.stack
        });
        throw bookingError; // Re-throw to be caught by outer catch
      }
      
      if (!createdBooking || !createdBooking.id) {
        console.error('❌ Booking was created but no ID returned:', createdBooking);
        throw new Error('فشل إنشاء الحجز - لم يتم استلام رقم الحجز');
      }
      
      console.log('✅ Booking ID:', createdBooking.id);

      // احتساب استخدام كود الخصم فعلياً بعد نجاح إنشاء الحجز فقط (لا يُحتسب عند مجرد
      // الضغط على "تطبيق" حتى لا تُستهلك حصّة الاستخدام من عملاء لم يكملوا الحجز).
      if (discountInfo) {
        discountCodeService
          .redeemCode(discountInfo.code, createdBooking.id, discountInfo.amount)
          .catch((err) => console.warn('Failed to redeem discount code (non-blocking):', err));
      }

      // Update customer profile preferences if needed (optional, don't fail if table doesn't exist)
      if (formData.allowWhatsApp !== undefined) {
        try {
          await supabase
            .from('customer_profiles')
            .upsert({
              user_id: currentUser.id,
              allow_whatsapp: formData.allowWhatsApp,
              updated_at: new Date().toISOString(),
            }, { onConflict: 'user_id' });
        } catch (profileError) {
          console.warn('Failed to update customer profile (table may not exist):', profileError);
          // Continue - not critical
        }
      }

      // مزامنة الاسم والبريد والجوال مع user_profiles لظهورها في لوحة العميل/المصور/المشرف
      try {
        const phoneNorm = normalizePhone(formData.phone);
        await supabase
          .from('user_profiles')
          .update({
            full_name: formData.fullName.trim(),
            email: formData.email.trim().toLowerCase(),
            ...(phoneNorm ? { phone: phoneNorm } : {}),
            updated_at: new Date().toISOString(),
          })
          .eq('id', currentUser.id);
      } catch (syncErr) {
        console.warn('Failed to sync user_profiles after booking:', syncErr);
      }

      setCreatedBookingId(createdBooking.id);
      clearDraft();

      // Note: WhatsApp notification is sent automatically by bookingService.createBooking
      // when a photographer is assigned via assign_next_photographer RPC.
      // If no photographer is assigned yet, booking-waiting page will send notification
      // when photographer is assigned.

      // Create in-app notification for customer (optional)
      try {
        const { notificationDatabaseService } = await import('../../services/notificationDatabaseService');
        await notificationDatabaseService.createNotification({
          user_id: currentUser.id,
          type: 'booking',
          title: 'تم إنشاء طلب حجز جديد',
          message: `تم إنشاء طلب حجزك بنجاح. جاري التحقق من توفر المصور...`,
          related_booking_id: createdBooking.id,
        });
      } catch (error) {
        console.warn('Failed to create notification (optional):', error);
        // Continue - notification is optional
      }

      // GA4: حدث إنشاء حجز جديد
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'booking_created', { event_category: 'booking', event_label: 'new_booking', value: 1 });
      }

      // Navigate directly to booking waiting page to complete the booking
      console.log('✅ Booking created successfully, navigating to booking waiting page:', createdBooking.id);
      navigate(`/booking-waiting/${createdBooking.id}`);
    } catch (error) {
      console.error('❌❌❌ Failed to create booking:', error);
      console.error('❌ Full error object:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        code: error?.code,
        details: error?.details,
        hint: error?.hint,
        name: error?.name,
        stack: error?.stack
      });
      
      // Show user-friendly error message
      let errorMessage = 'فشل إنشاء الحجز. يرجى المحاولة مرة أخرى.';
      
      if (error?.message?.includes('Not authenticated')) {
        errorMessage = 'يرجى تسجيل الدخول أولاً';
      } else if (error?.message?.includes('permission') || error?.code === '42501') {
        errorMessage = 'ليس لديك صلاحية لإنشاء حجز. يرجى التحقق من حسابك.';
      } else if (error?.message?.includes('city_id') || error?.message?.includes('category_id')) {
        errorMessage = 'خطأ في بيانات المدينة أو نوع التصوير. يرجى المحاولة مرة أخرى.';
      } else if (error?.message?.includes('scheduled_at') || error?.message?.includes('تاريخ')) {
        errorMessage = 'يرجى اختيار تاريخ ووقت صحيحين للحجز.';
      } else if (error?.message?.includes('السعر')) {
        errorMessage = 'خطأ في حساب السعر. يرجى المحاولة مرة أخرى.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      console.log('🔴 Showing error toast:', errorMessage);
      setToast({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  // If booking is created, show success screen
  if (bookingCreated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-12 text-center">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={48} className="text-success" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-4">تم إنشاء الحجز بنجاح!</h1>
              <p className="text-lg text-slate-600 mb-6">
                رقم الحجز: <span className="font-mono font-bold text-primary">{createdBookingId?.slice(0, 8).toUpperCase()}</span>
              </p>
              <div className="bg-slate-50 rounded-xl p-6 mb-6 text-right">
                <h3 className="font-semibold text-slate-900 mb-4">الخطوات التالية:</h3>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={18} className="text-success flex-shrink-0" />
                    <span>سيتم مراجعة طلبك من قبل المصور</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={18} className="text-success flex-shrink-0" />
                    <span>ستتلقى إشعاراً عند تأكيد الحجز</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Check" size={18} className="text-success flex-shrink-0" />
                    <span>سيتم إرسال رابط الدفع بعد التأكيد</span>
                  </li>
                </ul>
              </div>
              <div className="flex gap-4 justify-center flex-wrap">
                {createdBookingId && (
                  <Button
                    variant="default"
                    onClick={() => {
                      console.log('Navigating to booking waiting page:', createdBookingId);
                      navigate(`/booking-waiting/${createdBookingId}`);
                    }}
                    iconName="Clock"
                    iconPosition="right"
                  >
                    متابعة حالة الحجز
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/customer-dashboard/bookings')}
                  iconName="Calendar"
                >
                  عرض حجوزاتي
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/booking-wizard')}
                  iconName="Plus"
                  iconPosition="right"
                >
                  حجز جديد
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If no booking data, show error
  // SAFE MODE: Show loading while waiting for initial data load
  if (!initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (!bookingData || Object.keys(bookingData).length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-error" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">لا توجد بيانات حجز</h2>
          <p className="text-slate-600 mb-6">يرجى البدء من صفحة الحجز</p>
          <Button onClick={() => navigate('/booking-wizard')} iconName="ArrowRight">
            الذهاب إلى صفحة الحجز
          </Button>
        </div>
      </div>
    );
  }

  // Get photographer info if selected (avatar from results or draft for display and for passing to booking)
  const photographerId = bookingData.photographerId;
  const photographerName = bookingData.photographerName || 'سيتم تعيين مصور';
  const photographerAvatarUrl = bookingData.photographerAvatarUrl || null;
  const displayTotal = discountInfo ? discountInfo.total : calculatedPrice?.totalPrice;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4">
            <BackButton to="/booking-wizard" label="العودة إلى خطوات الحجز" />
          </div>
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">تأكيد الحجز</h1>
            <p className="text-slate-600">يرجى مراجعة التفاصيل وإكمال المعلومات المطلوبة</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Booking Summary */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">ملخص الحجز</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      clearDraft();
                      navigate('/booking-wizard');
                    }}
                    iconName="Edit"
                  >
                    تعديل
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">نوع الخدمة</p>
                    <p className="font-semibold text-slate-900">
                      {getDisplayLabel(bookingData, 'photographyType', bookingData.photographyType || bookingData.serviceType) || 'تصوير'}
                    </p>
                  </div>

                  {(bookingData.shootingMethod || bookingData.shootingMethodLabels?.length > 0) && (
                    <div className="py-3 border-b border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">وسيلة التصوير</p>
                      <p className="font-semibold text-slate-900">
                        {bookingData.shootingMethodLabels?.length > 0
                          ? bookingData.shootingMethodLabels.join('، ')
                          : getShootingMethodLabelAr(bookingData.shootingMethod)}
                      </p>
                    </div>
                  )}

                  {bookingData.workloadBucket && (
                    <div className="py-3 border-b border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">حجم العمل</p>
                      <p className="font-semibold text-slate-900">
                        {getWorkloadLabelAr(bookingData.photographyType || bookingData.serviceType, bookingData.workloadBucket)}
                      </p>
                    </div>
                  )}

                  {(bookingData.photographyType === 'product' || bookingData.serviceType === 'product') &&
                    bookingData.productPhysicalSizeBucket && (
                      <div className="py-3 border-b border-slate-200">
                        <p className="text-sm text-slate-600 mb-1">حجم المنتجات</p>
                        <p className="font-semibold text-slate-900 text-pretty">
                          {getProductPhysicalSizeLabelAr(bookingData.productPhysicalSizeBucket)}
                        </p>
                      </div>
                    )}

                  {bookingData.deliveredPhotosBucket && (
                    <div className="py-3 border-b border-slate-200">
                      <p className="text-sm text-slate-600 mb-1">الصور المسلّمة</p>
                      <p className="font-semibold text-slate-900">
                        {getDeliveredPhotosLabelAr(bookingData.deliveredPhotosBucket)}
                      </p>
                    </div>
                  )}

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">المدينة</p>
                    <p className="font-semibold text-slate-900">
                      {getDisplayLabel(bookingData, 'city', bookingData.city) || 'الرياض'}
                    </p>
                  </div>

                  {/* Shooting Location */}
                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">موقع التصوير</p>
                    {bookingData.shootingPlaceType === 'photographer_studio' ? (
                      <div>
                        <p className="font-semibold text-slate-900 mb-1">
                          التصوير في استديو المصور
                        </p>
                        {bookingData.photographerStudioLocationSnapshot ? (
                          <p className="text-sm text-slate-700">
                            {bookingData.photographerStudioLocationSnapshot.address_text || 
                             bookingData.photographerStudioLocationSnapshot.city || 
                             'سيتم تحديد العنوان بعد اختيار المصور'}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-500">
                            سيتم عرض عنوان الاستديو بعد اختيار المصور
                          </p>
                        )}
                      </div>
                    ) : bookingData.preciseLocation && bookingData.preciseLocation.address_text ? (
                      <div>
                        <p className="font-semibold text-slate-900">
                          {bookingData.preciseLocation.address_text}
                        </p>
                        {bookingData.preciseLocation.source === 'gps' && (
                          <p className="text-xs text-slate-500 mt-1">📍 باستخدام GPS</p>
                        )}
                        {bookingData.preciseLocation.source === 'search' && (
                          <p className="text-xs text-slate-500 mt-1">🔍 من البحث</p>
                        )}
                        {bookingData.preciseLocation.source === 'pin' && (
                          <p className="text-xs text-slate-500 mt-1">📍 من الخريطة</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-warning/5 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-warning">
                          <Icon name="AlertCircle" size={18} />
                          <p className="text-sm font-medium">الموقع الدقيق غير محدد</p>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">يرجى تحديد الموقع الدقيق في صفحة الحجز</p>
                      </div>
                    )}
                  </div>

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">التاريخ والوقت</p>
                    <p className="font-semibold text-slate-900">
                      {bookingData.date || bookingData.bookingDate
                        ? (() => {
                            const date = new Date(bookingData.date || bookingData.bookingDate);
                            const weekday = date.toLocaleDateString('ar-SA-u-ca-gregory', { weekday: 'long' });
                            const month = date.toLocaleDateString('ar-SA-u-ca-gregory', { month: 'long' });
                            const day = date.getDate();
                            const year = date.getFullYear();
                            return `${weekday}، ${day} ${month} ${year}`;
                          })()
                        : 'غير محدد'}{' '}
                      - {formatTimeToArabic(bookingData.time)}
                    </p>
                  </div>

                  <div className="py-3 border-b border-slate-200">
                    <p className="text-sm text-slate-600 mb-1">المدة</p>
                    <p className="font-semibold text-slate-900">
                      {getDisplayLabel(bookingData, 'duration', bookingData.duration) || 'غير محدد'}
                    </p>
                  </div>

                  {/* مستوى التعديل — يظهر فقط إذا طلب العميل الاحترافي */}
                  {(() => {
                    const EDITING_KEYS = ['basic-editing', 'professional-editing', 'no-editing'];
                    const editingKey = (bookingData.equipment || []).find(eq => EDITING_KEYS.includes(eq));
                    if (editingKey !== 'professional-editing') return null;
                    return (
                      <div className="py-3 border-b border-slate-200">
                        <p className="text-sm text-slate-600 mb-1">مستوى التعديل</p>
                        <p className="font-semibold text-slate-900">تعديل احترافي متقدم</p>
                      </div>
                    );
                  })()}

                  {/* الخدمات الإضافية — فقط غير خيارات التعديل */}
                  {(() => {
                    const EDITING_KEYS = ['basic-editing', 'professional-editing', 'no-editing'];
                    const equipmentLabels = {
                      'drone':     'تصوير بالطائرة المسيرة (Drone)',
                      'lighting':  'معدات إضاءة احترافية',
                      'video':     'تصوير فيديو',
                      'backdrop':  'خلفيات وديكورات',
                      'assistant': 'مساعد مصور',
                      'makeup':    'خدمة المكياج',
                    };
                    const otherEq = (bookingData.equipment || [])
                      .map((eq, idx) => ({ eq, idx }))
                      .filter(({ eq }) => !EDITING_KEYS.includes(eq));
                    if (otherEq.length === 0) return null;
                    return (
                      <div className="py-3 border-b border-slate-200">
                        <p className="text-sm text-slate-600 mb-1">الخدمات الإضافية</p>
                        <div className="flex flex-wrap gap-2">
                          {otherEq.map(({ eq, idx }) => {
                            const displayLabel = bookingData.equipmentLabels?.[idx] || equipmentLabels[eq] || eq;
                            return (
                              <span key={idx} className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm">
                                {displayLabel}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="py-3 flex items-center gap-4">
                    <div className="flex-shrink-0">
                      {(photographerAvatarUrl || photographerId) && (
                        <Image
                          src={photographerAvatarUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png'}
                          alt={photographerName}
                          className="w-14 h-14 rounded-xl object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600 mb-1">المصور</p>
                      <p className="font-semibold text-slate-900">{photographerName}</p>
                      {!photographerId && (
                        <p className="text-sm text-slate-500 mt-1">سيتم تعيين مصور مناسب لك</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Authentication Gate */}
              {!user && authStep !== 'complete' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">المصادقة</h2>

                  {authStep === 'method' && (
                    <div className="space-y-4">
                      <Input
                        label="الاسم الاول والاخير"
                        value={formData.fullName}
                        onChange={(e) => setFormData((prev) => ({ ...prev, fullName: e.target.value }))}
                        required
                        placeholder="ادخل اسمك هنا "
                      />
                      <Input
                        label="البريد الإلكتروني"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        required
                        placeholder="example@email.com"
                        dir="ltr"
                        className="text-left"
                      />
                      <div>
                        <Input
                          label="رقم الجوال"
                          type="tel"
                          inputMode="numeric"
                          value={formData.phone}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setFormData((prev) => ({ ...prev, phone: val }));
                          }}
                          placeholder="05xxxxxxxx"
                          maxLength={10}
                          dir="ltr"
                        />
                        {formData.phone && !normalizePhone(formData.phone) && (
                          <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                            رقم الجوال يجب أن يكون 10 أرقام ويبدأ بـ 05
                          </p>
                        )}
                      </div>
                      <Button
                        variant="default"
                        onClick={handleSendOTP}
                        loading={loading}
                        disabled={
                          !normalizePhone(formData.phone) ||
                          !validateCheckoutIdentityBeforeOtp(formData).ok
                        }
                        className="w-full"
                        iconName="Send"
                      >
                        إرسال رمز التحقق
                      </Button>
                    </div>
                  )}

                  {authStep === 'verify' && (
                    <div className="space-y-4">
                      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 space-y-1">
                        <p>
                          <span className="text-slate-500">الاسم:</span>{' '}
                          <span className="font-medium">{formData.fullName.trim() || '—'}</span>
                        </p>
                        <p dir="ltr" className="text-left">
                          <span className="text-slate-500">البريد:</span>{' '}
                          <span className="font-medium">{formData.email.trim() || '—'}</span>
                        </p>
                        <p dir="ltr" className="text-left">
                          <span className="text-slate-500">الجوال:</span>{' '}
                          <span className="font-semibold">{normalizePhone(formData.phone) || formData.phone}</span>
                        </p>
                      </div>
                      <p className="text-slate-700">
                        تم إرسال رمز التحقق إلى{' '}
                        <span className="font-semibold">{normalizePhone(formData.phone) || formData.phone}</span>
                      </p>
                      <Input
                        label="رمز التحقق"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        name="one-time-code"
                        value={otpCode}
                        onChange={(e) => setOtpCode(normalizeOtpValue(e.target.value))}
                        placeholder="0000"
                        maxLength={4}
                        dir="ltr"
                        className="text-center text-2xl font-mono"
                      />
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setAuthStep('method');
                            setOtpCode('');
                            setOtpSent(false);
                          }}
                          className="flex-1"
                        >
                          إلغاء
                        </Button>
                        <Button
                          variant="default"
                          onClick={handleVerifyOTP}
                          loading={verifying}
                          disabled={otpCode.length < 4}
                          className="flex-1"
                          iconName="Check"
                        >
                          التحقق
                        </Button>
                      </div>
                      <button
                        onClick={handleSendOTP}
                        className="text-sm text-primary hover:underline"
                      >
                        إعادة إرسال الرمز
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Customer Information */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">معلومات العميل</h2>
                <div className="space-y-4">
                  {(user || authStep === 'complete') && (
                    <>
                  <Input
                    label="الاسم الاول والاخير"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                    disabled={!!user}
                  />
                  <Input
                    label="رقم الهاتف"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    disabled={!!user}
                  />
                  <Input
                    label="البريد الإلكتروني"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!!user}
                  />
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      ملاحظات للمصور (اختياري)
                    </label>
                    <textarea
                      value={formData.invoiceNote}
                      onChange={(e) => setFormData({ ...formData, invoiceNote: e.target.value })}
                      rows={3}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="مثال:  لديّ طلب خاص..."
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="whatsapp"
                      checked={formData.allowWhatsApp}
                      onChange={(e) => setFormData({ ...formData, allowWhatsApp: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <label htmlFor="whatsapp" className="text-sm text-slate-700 cursor-pointer">
                      السماح بتحديثات واتساب
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Price Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-slate-900">ملخص السعر</h3>
                  {priceLocked && (
                    <div className="flex items-center gap-1 text-success">
                      <Icon name="Lock" size={16} />
                      <span className="text-xs font-medium">مقفل</span>
                    </div>
                  )}
                </div>

                {priceLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                      <p className="text-sm text-slate-600">جاري حساب السعر...</p>
                    </div>
                  </div>
                ) : calculatedPrice ? (
                  <>
                    <div className="space-y-3 mb-4">
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>رسوم المصور (80%)</span>
                          <span>{formatNumber(Math.round((displayTotal || 0) * 0.80))} ر.س</span>
                        </div>
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>رسوم المنصة (20%)</span>
                          <span>{formatNumber(Math.round((displayTotal || 0) * 0.20))} ر.س</span>
                        </div>
                        {discountInfo && (
                          <div className="flex justify-between text-sm text-success font-medium">
                            <span>خصم ({discountInfo.code} - {discountInfo.percent}%)</span>
                            <span>- {formatNumber(discountInfo.amount)} ر.س</span>
                          </div>
                        )}
                      </div>
                      <div className="border-t-2 border-slate-300 pt-3 flex justify-between font-bold text-slate-900">
                        <span>الإجمالي</span>
                        <span className="text-primary text-2xl">
                          {formatNumber(displayTotal)} ر.س
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 text-left mt-1">السعر النهائي شامل جميع الرسوم</p>
                    </div>

                    {/* Discount Code */}
                    <div className="mb-6">
                      {!discountInfo ? (
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-2">لديك كود خصم؟</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={discountCodeInput}
                              onChange={(e) => {
                                setDiscountCodeInput(e.target.value.toUpperCase());
                                setDiscountError('');
                              }}
                              placeholder="أدخل الكود"
                              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-primary"
                              dir="ltr"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleApplyDiscountCode}
                              loading={discountApplying}
                              disabled={discountApplying || !discountCodeInput.trim()}
                            >
                              تطبيق
                            </Button>
                          </div>
                          {discountError && <p className="text-xs text-red-500 mt-1.5">{discountError}</p>}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between bg-success/10 border border-success/20 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <Icon name="Tag" size={16} className="text-success flex-shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-success" dir="ltr">{discountInfo.code} — خصم {discountInfo.percent}%</p>
                              <p className="text-xs text-slate-500">وفّرت {formatNumber(discountInfo.amount)} ر.س</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveDiscountCode}
                            className="text-xs text-slate-400 hover:text-red-500 flex-shrink-0"
                          >
                            إزالة
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
                      <div className="flex items-start gap-2">
                        <Icon name="Lock" size={16} className="text-success flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-success mb-1">السعر مقفل</p>
                          <p className="text-xs text-slate-600">
                            هذا السعر النهائي والمضمون. لن يتغير بعد التأكيد.
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="AlertCircle" size={32} className="mx-auto mb-2 text-warning" />
                    <p className="text-sm text-slate-600">لم يتم حساب السعر بعد</p>
                  </div>
                )}

                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-slate-600">
                      البيانات الخاصة بك محمية ولن يتم مشاركتها مع أطراف ثالثة.
                    </p>
                  </div>
                </div>
                {/* Debug: Show why button is disabled */}
                {(saving || priceLoading || (!user && authStep !== 'complete') || !formData.fullName || !formData.phone) && (
                  <div className="mb-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    {saving && <div>• جاري الحفظ...</div>}
                    {priceLoading && <div>• جاري حساب السعر...</div>}
                    {(!user && authStep !== 'complete') && <div>• يرجى تسجيل الدخول أو إكمال التحقق</div>}
                    {!formData.fullName && <div>• يرجى إدخال الاسم</div>}
                    {!formData.phone && <div>• يرجى إدخال رقم الهاتف</div>}
                  </div>
                )}
                <Button
                  variant="default"
                  size="lg"
                  fullWidth
                  onClick={() => {
                    console.log('🔵 Button clicked!');
                    console.log('🔵 Current state:', {
                      saving,
                      priceLoading,
                      user: !!user,
                      userProfile: !!userProfile,
                      authStep,
                      fullName: formData.fullName,
                      phone: formData.phone,
                      calculatedPrice: !!calculatedPrice,
                      priceLocked,
                      bookingDataPrice: bookingData.price,
                      shootingPlaceType: bookingData.shootingPlaceType,
                      preciseLocation: bookingData.preciseLocation
                    });
                    handleConfirmBooking();
                  }}
                  loading={saving}
                  disabled={
                    saving || 
                    priceLoading ||
                    (!user && authStep !== 'complete') || 
                    !formData.fullName || 
                    !formData.phone
                  }
                  iconName="Check"
                  iconPosition="right"
                >
                  {saving ? 'جاري الحفظ...' : 'تأكيد الحجز'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Checkout;
